import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "@/i18n";

export default function Footer() {
  const { t } = useTranslation();
  const { lang: paramLang } = useParams();
  const lang = SUPPORTED_LANGS.includes(paramLang) ? paramLang : DEFAULT_LANG;
  const homeHref = `/${lang}`;

  const openCookieSettings = () => {
    window.dispatchEvent(new CustomEvent("open-cookie-settings"));
  };

  return (
    <footer
      className="mt-auto"
      style={{ backgroundColor: "var(--anv-ink)", color: "#fff" }}
      data-testid="site-footer"
    >
      <div className="anv-container py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-8">
          {/* Brand + tagline */}
          <div className="md:col-span-5">
            <div className="font-display text-2xl font-semibold tracking-[0.18em]">
              {t("header.brand")}
            </div>
            <p className="mt-4 text-white/70 text-sm leading-relaxed max-w-md">
              {t("footer.tagline")}
            </p>
            <div className="mt-6 text-[13px] leading-relaxed text-white/60 space-y-1">
              <div>{t("footer.legal.companyName")}</div>
              <div>{t("footer.legal.address")}</div>
              <div>{t("footer.legal.piva")} · {t("footer.legal.cf")}</div>
              <div>{t("footer.legal.rea")}</div>
              <div>{t("footer.legal.capital")}</div>
              <div>{t("footer.legal.pec")}</div>
              <div>{t("footer.legal.jurisdiction")}</div>
              <div>{t("footer.legal.founded")}</div>
              <div className="pt-1 text-white/40 text-xs">{t("footer.legal.license")}</div>
            </div>
          </div>

          {/* Columns */}
          <div className="md:col-span-3">
            <div className="anv-eyebrow text-white/50" style={{ color: "rgba(255,255,255,0.5)" }}>
              {t("footer.columns.company")}
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <a href={`${homeHref}#services`} className="text-white/80 hover:text-white no-underline" data-testid="footer-link-services">
                  {t("footer.links.services")}
                </a>
              </li>
              <li>
                <a href={`${homeHref}#about`} className="text-white/80 hover:text-white no-underline" data-testid="footer-link-about">
                  {t("footer.links.about")}
                </a>
              </li>
              <li>
                <a href={`${homeHref}#solutions`} className="text-white/80 hover:text-white no-underline" data-testid="footer-link-solutions">
                  {t("footer.links.solutions")}
                </a>
              </li>
              <li>
                <a href={`${homeHref}#contact`} className="text-white/80 hover:text-white no-underline" data-testid="footer-link-contact">
                  {t("footer.links.contact")}
                </a>
              </li>
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="anv-eyebrow" style={{ color: "rgba(255,255,255,0.5)" }}>
              {t("footer.columns.legal")}
            </div>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <Link to={`${homeHref}/privacy`} className="text-white/80 hover:text-white no-underline" data-testid="footer-link-privacy">
                  {t("footer.links.privacy")}
                </Link>
              </li>
              <li>
                <Link to={`${homeHref}/cookie-policy`} className="text-white/80 hover:text-white no-underline" data-testid="footer-link-cookie">
                  {t("footer.links.cookie")}
                </Link>
              </li>
              <li>
                <Link to={`${homeHref}/terms`} className="text-white/80 hover:text-white no-underline" data-testid="footer-link-terms">
                  {t("footer.links.terms")}
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openCookieSettings}
                  className="text-white/80 hover:text-white text-left bg-transparent border-0 p-0 cursor-pointer"
                  data-testid="footer-link-cookie-settings"
                >
                  {t("footer.links.cookieSettings")}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row justify-between gap-3">
          <div className="text-xs text-white/50">{t("footer.copyright")}</div>
          <div className="text-xs text-white/50">
            <a href="mailto:hello@anvoriq.com" className="text-white/60 hover:text-white no-underline" data-testid="footer-email">
              hello@anvoriq.com
            </a>
            <span className="mx-2">·</span>
            <a href="tel:+390283756120" className="text-white/60 hover:text-white no-underline" data-testid="footer-phone">
              +39 02 8375 6120
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
